<script setup lang="ts">
import { isShowTabsShadow } from '@/shared/utils/isShowTabsShadow';
import type { Props, Emits } from '@/shared/ui/AppTabs/model/types/appTabs';

defineOptions({ name: 'AppTabs' });

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  maxWidth: '100%',
  isRenderInDom: false,
  isShowSideShadow: false,
});

const emit = defineEmits<Emits>();

const isMobile: boolean = useIsMobile();
const groupName: string = useId();
const listRef = ref<HTMLElement | null>(null);
const itemRefs = ref<HTMLElement[]>([]);
const isScrollable: Ref<boolean> = ref(false);
const isShowShadow: Ref<boolean> = ref(false);
const isIndicatorReady: Ref<boolean> = ref(false);
const indicatorStyles: Ref<Record<string, string>> = ref({});

let itemMetrics: { width: number; left: number }[] = [];
let measureRafId: number | null = null;
let resizeObserver: ResizeObserver | null = null;

const itemStyles: ComputedRef<string> = computed(() => `max-width: ${props.maxWidth}`);

const updateScrollable = (): void => {
  const el = listRef.value;
  if (!el) return;
  isScrollable.value = el.scrollWidth > el.clientWidth;
};

const scrollSpy = (): void => {
  if (!props.isShowSideShadow) return;
  isShowShadow.value = isShowTabsShadow(listRef.value);
};

const measureItems = (): void => {
  if (props.variant !== 'indicator') return;
  itemMetrics = itemRefs.value
    .map((el) => ({ width: el.offsetWidth, left: el.offsetLeft }))
    .sort((a, b) => a.left - b.left);
};

const updateIndicator = (): void => {
  if (props.variant !== 'indicator') return;
  const activeIndex = props.tabList.findIndex((tab) => tab.value === props.modelValue);
  const metrics = itemMetrics[activeIndex];
  if (!metrics) return;
  indicatorStyles.value = {
    width: `${metrics.width}px`,
    transform: `translateX(${metrics.left}px)`,
  };
  if (!isIndicatorReady.value) {
    requestAnimationFrame(() => {
      isIndicatorReady.value = true;
    });
  }
};

const selectTab = (value: string | number): void => {
  if (value === props.modelValue) return;
  emit('update:modelValue', value);
  emit('change', value);
};

const measure = (): void => {
  updateScrollable();
  measureItems();
  updateIndicator();
  scrollSpy();
};

const scheduleMeasure = (): void => {
  if (measureRafId !== null) return;
  measureRafId = requestAnimationFrame(() => {
    measureRafId = null;
    measure();
  });
};

onMounted(() => {
  const el = listRef.value;
  if (!el) return;

  resizeObserver = new ResizeObserver(measure);
  resizeObserver.observe(el);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  if (measureRafId !== null) cancelAnimationFrame(measureRafId);
});

watch(() => props.modelValue, updateIndicator);

watch(() => [props.tabList, props.variant], scheduleMeasure, {
  deep: true,
  flush: 'post',
});
</script>

<template>
  <div
    :class="[
      'app-tabs',
      `_${variant}`,
      { _mobile: isMobile },
      { _scrollable: isScrollable },
      { _shadow: isShowShadow },
      { _ready: isIndicatorReady },
    ]"
  >
    <div class="app-tabs__list-wrapper">
      <div
        v-if="variant === 'indicator' && !isMobile"
        class="app-tabs__indicator"
        :style="indicatorStyles"
      ></div>
      <ul ref="listRef" class="app-tabs__list" @scroll="scrollSpy">
        <li
          v-for="(tab, index) in tabList"
          :key="tab.id"
          ref="itemRefs"
          :class="['app-tabs__item', { _active: tab.value === modelValue }]"
          :style="itemStyles"
        >
          <label class="app-tabs__item-label" :for="`${tab.value}-${tab.id}`">
            <span>{{ tab.label }}</span>
            <slot
              name="tab-extra"
              :tab="tab"
              :index="index"
              :is-active="tab.value === modelValue"
            ></slot>
          </label>
          <input
            :id="`${tab.value}-${tab.id}`"
            class="app-tabs__item-input"
            type="radio"
            :name="`${groupName}-tab`"
            :value="tab.value"
            :checked="tab.value === modelValue"
            @click="selectTab(tab.value)"
            @change="selectTab(tab.value)"
          />
        </li>
      </ul>
    </div>
    <slot name="header"></slot>
    <template v-if="isRenderInDom">
      <div v-for="(tab, index) in tabList" :key="tab.id" class="app-tabs__content">
        <div v-show="tab.value === modelValue" class="app-tabs__panel">
          <slot :name="`tabPanel-${index + 1}`"></slot>
        </div>
      </div>
    </template>
    <template v-else>
      <template v-for="(tab, index) in tabList" :key="tab.id">
        <div v-if="tab.value === modelValue" class="app-tabs__panel">
          <slot :name="`tabPanel-${index + 1}`"></slot>
        </div>
      </template>
    </template>
  </div>
</template>

<style lang="less">
.app-tabs {
  &__list-wrapper {
    position: relative;
  }

  &__list {
    display: flex;
    align-items: center;
    margin-bottom: -1px;
  }

  &__indicator {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 5px;
    background-color: @color-persimmon;
    z-index: 2;
    pointer-events: none;
    opacity: 0;
  }

  &._ready &__indicator {
    opacity: 1;
    transition:
      transform @A ease-in-out,
      width @A ease-in-out;
  }

  &__item {
    position: relative;
    flex: 1 1 auto;
    text-align: center;
    white-space: nowrap;
    box-sizing: content-box;

    &._active &__amount._text {
      color: @color-elephant;
    }
  }

  &._default &__item {
    font-weight: 600;
    font-size: @size-x6;
    line-height: @size-x7;
    background: #fff;
    border: 1px solid @color-gallery;
    color: @color-dove-gray;
    box-shadow: 0px @size-x2 @size-x6 -18px rgba(129, 135, 189, 0.15);
    border-radius: @main-size @main-size 0 0;

    &._active {
      box-shadow: -4px -18px 25px rgba(0, 0, 0, 0.06);
      border: 1px solid #fff;
      font-weight: 600;
      color: @color-elephant;
    }
  }

  &__item-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: @size-x3;
    width: 100%;
    cursor: pointer;
    padding: @size-x3;
    position: relative;
  }

  &__item-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &__amount {
    &._circle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: @size-x6;
      min-height: @size-x6;
      font-size: 10px;
      line-height: @size-x3;
      font-weight: 600;
      color: #fff;
      background-color: @color-persimmon;
      border-radius: 100px;
      padding: 3px 7px;
    }

    &._text {
      font-size: 10px;
      font-weight: 600;
      line-height: 130%;
      color: @color-silver-chalice;
    }
  }

  &._underlined &__list {
    gap: @size-x2;
  }

  &._underlined &__item {
    font-weight: 600;
    font-size: @size-x4;
    line-height: 22px;
    background: #fff;
    border: 1px solid @color-gallery;
    color: @color-dove-gray;
    box-shadow: 0px @size-x2 @size-x6 -18px rgba(129, 135, 189, 0.15);
    border-radius: @main-size;
    display: flex;
    justify-content: center;
  }

  &._underlined &__item-label {
    width: 100%;
  }

  &._underlined &__item-label span {
    position: relative;
  }

  &._underlined &__item._active {
    border: 1px solid @color-gallery;
    border-radius: 4px;
    box-shadow: 0px 2px 16px 2px rgba(129, 135, 189, 0.12);
    color: @color-elephant;
  }

  &._underlined &__item._active &__item-label span::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -@main-size;
    border: 1px solid @color-persimmon;
    width: 100%;
  }

  &._indicator &__list {
    gap: @size-x10;
    margin-bottom: @size-x5;
    border-bottom: 1px solid @color-silver-chalice;
  }

  &._indicator &__item {
    flex: none;
    font-size: @size-x4;
    line-height: @size-x6;
    font-weight: 700;
    color: @color-silver-chalice;

    &._active {
      color: @color-elephant;
    }
  }

  &._indicator &__item-label {
    padding-left: 0;
    padding-right: 0;
  }

  &._indicator:not(._mobile) &__item._active::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 5px;
    background-color: @color-persimmon;
    z-index: 2;
    pointer-events: none;
  }

  &._indicator:not(._mobile)._ready &__item._active::after {
    display: none;
  }

  &._mobile &__item {
    font-weight: 600;
    font-size: @size-x4;
    line-height: 22px;
    padding: 0 10px;

    &._active {
      font-weight: 600;
    }
  }

  &._mobile &__item-label {
    padding: @size-x3 0;
  }

  &._indicator._mobile &__list {
    border-bottom: none;
  }

  &._indicator._mobile &__item {
    font-size: @size-x4;
    line-height: 22px;
    padding: 0;
    padding-bottom: @size-x4;

    &._active::after {
      content: '';
      position: absolute;
      bottom: 3px;
      right: 0;
      width: 100%;
      height: 5px;
      background-color: @color-persimmon;
      z-index: 2;
    }
  }

  &._indicator._mobile &__item-label {
    justify-content: flex-start;
    position: relative;
    margin-bottom: -14px;

    &::after {
      content: '';
      position: absolute;
      bottom: 3px;
      right: 0;
      width: 300%;
      height: 1px;
      background-color: @color-silver-chalice;
      z-index: 1;
    }
  }

  &._mobile._shadow {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 0;
      height: 0;
      box-shadow: @size-x3 @size-x3 @size-x3 @size-x8 #fff;
      z-index: 1;
    }
  }

  &._scrollable &__list,
  &._mobile &__list {
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    scroll-snap-type: x proximity;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &._scrollable &__item,
  &._mobile &__item {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }
}
</style>
